import React, { FC } from 'react';
import Image from 'next/image';
import { GenExternalLink } from '@/components';
import { isItemSkipRequested } from '@/lib/api/subscriptionManager/helpers';
import {
  getAssetByKind,
  KIT_ASSET_INDICES,
} from '@/lib/api/productCatalog/helpers';
import { TypeProductDecoratedScheduledItem } from '@/lib/api/subscriptionManager/types';
import { generateZendeskLink } from '@/utils/zendesk';
import {
  formatProductAgeBand,
  interpolate,
  TypeAccountSettingsPageTranslations,
} from '@/utils/translations';
import { _getBillingDate, _getFormattedScheduledDate } from './utils';

type NextKitPreviewProps = {
  t: TypeAccountSettingsPageTranslations;
  locale: string | undefined;
  nextUnprocessedItem: TypeProductDecoratedScheduledItem;
};

const NextKitShippingAndBilling: FC<NextKitPreviewProps> = ({
  t,
  locale,
  nextUnprocessedItem,
}) => {
  return (
    <>
      <p className="mb-1">
        {`${t.shipDateLabel} ${_getFormattedScheduledDate(
          nextUnprocessedItem,
          locale,
        )}`}
      </p>
      <p>
        {`${t.billDateLabel} ${_getBillingDate(
          nextUnprocessedItem,
          locale,
          t.prepaidLabel,
        )}`}
      </p>
    </>
  );
};

const NextKitSkippedMessaging: FC<NextKitPreviewProps> = ({
  t,
  locale,
  nextUnprocessedItem,
}) => {
  const zendeskLinkUrl = generateZendeskLink(locale);

  const ZendeskLink = () => {
    return (
      <GenExternalLink
        href={zendeskLinkUrl}
        cta={t.contactUs}
        blankTarget
        noFollow
      />
    );
  };

  return (
    <p>
      {`${t.skippedKitMessagePart1} `}
      <ZendeskLink />
      {` ${interpolate(t.skippedKitMessagePart2, {
        skipDateCutoff: _getFormattedScheduledDate(nextUnprocessedItem, locale),
      })}`}
    </p>
  );
};

const NextPlayKitDetails: FC<NextKitPreviewProps> = ({
  t,
  locale,
  nextUnprocessedItem,
}) => {
  const { assets, name, metadata } = nextUnprocessedItem.product || {};
  const { age_unit, min_age, max_age } = metadata?.data || {};
  const image = getAssetByKind(assets, KIT_ASSET_INDICES.CLOSED_BOX);

  return (
    <div className="flex flex-row">
      {image && (
        <Image
          alt={image.alt_text || name || ''}
          src={image.uri}
          width={125}
          height={125}
          className="h-[125px] w-[125px]"
        />
      )}
      <div>
        <p className="mb-2 font-bold">{name}</p>
        <p className="mb-4 font-bold">
          ({formatProductAgeBand(age_unit, min_age, max_age, locale)})
        </p>
        {isItemSkipRequested(nextUnprocessedItem) ? (
          <NextKitSkippedMessaging
            t={t}
            locale={locale}
            nextUnprocessedItem={nextUnprocessedItem}
          />
        ) : (
          <NextKitShippingAndBilling
            t={t}
            locale={locale}
            nextUnprocessedItem={nextUnprocessedItem}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(NextPlayKitDetails);
